import type { Meta, MetaPreview, Video } from '../types'

export function releasedThisWeek(items: MetaPreview[], now = Date.now(), days = 7) {
  const start = now - days * 24 * 60 * 60 * 1000
  const seen = new Set<string>()
  return items
    .filter((item) => {
      const released = item.released ? Date.parse(item.released) : 0
      const key = `${item.type}:${item.id}`
      if (!released || released < start || released > now || seen.has(key)) return false
      seen.add(key)
      return true
    })
    .sort((a, b) => Date.parse(b.released || '') - Date.parse(a.released || ''))
}

/** Next episode after the most recently completed one. Part-watched titles are
 * intentionally handled by Continue watching instead, so this never duplicates
 * that row. */
export function nextVideoAfterHistory(meta: Meta, watched: Record<string, number>): Video | undefined {
  const videos = (meta.videos || []).filter((video) => video.season !== 0)
  let lastIndex = -1
  let newest = -1
  videos.forEach((video, index) => {
    const completedAt = watched[`${meta.type}:${meta.id}:${video.id}`] || 0
    if (completedAt > newest) { newest = completedAt; lastIndex = index }
  })
  return lastIndex >= 0 ? videos[lastIndex + 1] : undefined
}

/**
 * The span a title covers. Cinemeta gives a movie one year but a series a range
 * — "2019–2023", or "2019–" while it is still running — and `Number()` returns
 * NaN for both, which used to drop every ranged series from a year filter.
 */
export function titleYears(item: MetaPreview, now = new Date().getFullYear()) {
  const text = String(item.year ?? item.releaseInfo ?? item.released ?? '')
  const found = text.match(/\d{4}/g)?.map(Number) ?? []
  const start = found[0] || 0
  if (!start) return { start: 0, end: 0 }
  // a trailing dash with no second year means "still running"
  const running = /\d{4}\s*[\u2010-\u2015-]\s*$/.test(text.trim())
  return { start, end: running ? Math.max(start, now) : (found[1] || start) }
}

/** Does this title cover any part of the requested span? */
export function coversYears(item: MetaPreview, from: number, to: number, now?: number) {
  const { start, end } = titleYears(item, now)
  return Boolean(start) && start <= to && end >= from
}
